import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { orderingQuotationService } from '../services/orderingQuotationService';
import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import { Modal } from '../../../components/ui';
import { PdfViewer } from '../components/pdfviewer';
import { downloadPdfFromUrl } from '../utils/downloadPdf';
import { toast } from 'react-hot-toast';
import { useOrderingFilters } from '../hooks/useOrderingFilters';
import { OrderingControlPanel } from '../components/control_pannel';
import Pagination from '../../../components/ui/Pagination';
import { exportToExcel } from '../../../utils/exportUtils';
import {
  ArrowLeft, Plus, Clock, Eye, Download, Edit, ArrowRight,
  Package, Truck, CheckCircle, AlertCircle, Calendar, MapPin,
  Phone, Mail, User, ShieldCheck, History
} from 'lucide-react';

const ORDER_STATUSES = [
  'Order Placed',
  'Processing',
  'Dispatched',
  'In Transit',
  'Delivered',
  'Cancelled'
];

export const LeadOrderingWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadInfo, setLeadInfo] = useState(location.state?.lead || null);

  const {
    search, setSearch,
    filters, setFilters,
    sortBy, setSortBy,
    filteredData,
    paginatedData,
    currentPage,
    setCurrentPage,
    totalPages,
    showPagination
  } = useOrderingFilters(quotations);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Status update loading state
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  // Status History Modal State
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);

  // PDF Modal State
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPdfDoc, setSelectedPdfDoc] = useState(null);

  const fetchQuotations = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch all quotations since this is a centralized Master Orderings workspace
      const res = await orderingQuotationService.getQuotations();
      let data = res.success && res.data ? res.data : (Array.isArray(res) ? res : []);

      setQuotations(data);

      // Extract/refresh leadInfo from quotations if not passed in location state
      if (data.length > 0 && !leadInfo) {
        const latest = data[0];
        setLeadInfo({
          _id: id,
          customId: latest.customId || latest.contactObj?.customId || '-',
          projectName: latest.projectName || latest.contactObj?.customerName || '',
          site: latest.site || '',
          contactObj: latest.contactObj || null,
          referenceDesigner: latest.referenceDesigner || '',
          leadId: latest.leadId || latest.clientId || id
        });
      }
    } catch (error) {
      console.error("Error fetching quotations for lead:", error);
      toast.error("Failed to load quotations");
    } finally {
      setLoading(false);
    }
  }, [id, leadInfo]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  const handleOrderStatusChange = async (quotationId, newStatus) => {
    try {
      setUpdatingStatusId(quotationId);
      const res = await orderingQuotationService.updateOrderStatus(quotationId, newStatus, `Order status changed to ${newStatus}`);
      if (res.success) {
        toast.success(`Order status updated to ${newStatus}`);
        // Update locally without full reload for instant snappy feedback
        setQuotations(prev => prev.map(q => {
          if (q._id === quotationId) {
            const history = q.statusHistory ? [...q.statusHistory] : [];
            history.push({
              status: newStatus,
              updatedAt: new Date().toISOString(),
              updatedBy: 'Admin',
              note: `Order status changed to ${newStatus}`
            });
            return { ...q, orderStatus: newStatus, statusHistory: history };
          }
          return q;
        }));
      } else {
        toast.error(res.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating order status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleNewQuotation = () => {
    const leadToPass = leadInfo || { _id: id, id: id };
    navigate('/ordering/create', {
      state: {
        createNewQuotationForLead: leadToPass,
        leadId: id
      }
    });
  };

  const handleManageQuotation = (row) => {
    navigate('/ordering/create', { state: { editQuotation: row } });
  };

  const handleViewPdf = (row) => {
    if (row.pdfUrl) {
      setSelectedPdfDoc({
        fileUrl: row.pdfUrl,
        title: `Quotation_${row.quotationNo || 'VK'}.pdf`
      });
      setIsViewModalOpen(true);
    }
  };

  const handleOpenHistory = (row, e) => {
    e.stopPropagation();
    setSelectedHistoryRow(row);
    setHistoryModalOpen(true);
  };

  // Helper to extract customer display information
  const getCustomerDisplay = () => {
    const c = leadInfo?.contactObj || quotations[0]?.contactObj || {};
    const name = c.customerName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || leadInfo?.projectName || quotations[0]?.projectName || 'Customer / Project';
    const phone = c.mobile || c.phoneNumber || c.phone || '';
    const email = c.email || '';
    const locationStr = c.city ? `${c.city}, ${c.state || ''}`.replace(/^,\s*|,\s*$/g, '') : (leadInfo?.site || quotations[0]?.site || '-');
    const verveId = leadInfo?.customId || quotations[0]?.customId || c.customId || '-';
    const designer = leadInfo?.referenceDesigner || quotations[0]?.referenceDesigner || '-';

    return { name, phone, email, locationStr, verveId, designer };
  };

  const customer = getCustomerDisplay();

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const exportData = filteredData.map(item => ({
        'Quotation Number': item.quotationNo || 'Draft',
        'Category': item.category || 'Appliances',
        'Created Date': item.date || (item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'),
        'Grand Total': item.totals?.grandTotal || 0,
        'Approval Status': item.status || 'Draft',
        'Order Status': item.orderStatus || 'Order Placed'
      }));
      await exportToExcel(exportData, { filename: `Quotations_${customer.verveId || 'Export'}` });
      toast.success("Export started successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    {
      header: 'Quotation Number',
      accessor: (row) => row.quotationNo || 'Draft / Not Generated',
      cellClassName: '!font-medium text-brand-blue !whitespace-nowrap !py-1'
    },
    {
      header: 'Category',
      accessor: (row) => (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
          {row.category || 'Appliances'}
        </span>
      ),
      cellClassName: '!whitespace-nowrap !py-1'
    },
    {
      header: 'Created Date',
      accessor: (row) => row.date || (row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'),
      cellClassName: '!font-normal !whitespace-nowrap text-slate-600 text-xs !py-1'
    },
    {
      header: 'Grand Total',
      accessor: (row) => `₹${(row.totals?.grandTotal || 0).toLocaleString('en-IN')}`,
      cellClassName: '!font-semibold text-slate-800 !whitespace-nowrap !py-1'
    },
    {
      header: 'Approval Status',
      accessor: (row) => (
        <span className={`px-2.5 py-1 border rounded-md text-[11px] font-semibold ${row.status === 'Approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          row.status === 'Rejected' ? 'bg-red-50 text-red-700 border-red-200' :
            row.status === 'Pending' ? 'bg-amber-50 text-amber-700 border-amber-200' :
              'bg-slate-50 text-slate-600 border-slate-200'
          }`}>
          {row.status === 'Pending' ? 'Pending Approval' : row.status || 'Draft'}
        </span>
      ),
      cellClassName: '!whitespace-nowrap !py-1'
    },
    {
      header: 'Order Status',
      accessor: (row) => {
        const currentOrderStatus = row.orderStatus || 'Order Placed';
        return (
          <span className={`px-2.5 py-1 border rounded-md text-[11px] font-semibold ${currentOrderStatus === 'Delivered' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
              currentOrderStatus === 'Dispatched' ? 'bg-blue-50 text-brand-blue border-blue-200' :
                currentOrderStatus === 'In Transit' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                  currentOrderStatus === 'Processing' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    currentOrderStatus === 'Cancelled' ? 'bg-red-50 text-red-700 border-red-200' :
                      'bg-slate-50 text-slate-700 border-slate-200'
            }`}>
            {currentOrderStatus}
          </span>
        );
      },
      cellClassName: '!whitespace-nowrap !py-1'
    },
    {
      header: 'Update Status',
      accessor: (row) => {
        const currentOrderStatus = row.orderStatus || 'Order Placed';
        const isUpdating = updatingStatusId === row._id;

        return (
          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
            <select
              value={currentOrderStatus}
              disabled={isUpdating}
              onChange={(e) => handleOrderStatusChange(row._id, e.target.value)}
              className={`text-[12px] font-semibold px-3 py-1.5 rounded-lg border outline-none cursor-pointer transition-all ${currentOrderStatus === 'Delivered' ? 'bg-emerald-50 text-emerald-700 border-emerald-300 ring-1 ring-emerald-200' :
                currentOrderStatus === 'Dispatched' ? 'bg-blue-50 text-brand-blue border-blue-300 ring-1 ring-blue-200' :
                  currentOrderStatus === 'In Transit' ? 'bg-purple-50 text-purple-700 border-purple-300 ring-1 ring-purple-200' :
                    currentOrderStatus === 'Processing' ? 'bg-amber-50 text-amber-700 border-amber-300 ring-1 ring-amber-200' :
                      currentOrderStatus === 'Cancelled' ? 'bg-red-50 text-red-700 border-red-300 ring-1 ring-red-200' :
                        'bg-slate-50 text-slate-700 border-slate-300 hover:bg-slate-100'
                } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {ORDER_STATUSES.map((st) => (
                <option key={st} value={st} className="bg-white text-slate-800 font-medium">
                  {st}
                </option>
              ))}
            </select>
          </div>
        );
      },
      cellClassName: '!whitespace-nowrap !py-1'
    },
    {
      header: 'Status History',
      accessor: (row) => (
        <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => handleOpenHistory(row, e)}
            className="p-1.5 bg-slate-100 hover:bg-brand-blue/10 text-slate-500 hover:text-brand-blue rounded-md transition-colors"
            title="View Timestamped Status History"
          >
            <History size={16} />
          </button>
        </div>
      ),
      cellClassName: '!whitespace-nowrap text-center !py-1'
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {/* <Button
            variant="primary"
            size="sm"
            onClick={() => handleManageQuotation(row)}
            className="!px-3 !py-1.5 !text-xs !bg-brand-blue hover:!bg-brand-blue/90 flex items-center gap-1.5 shadow-sm"
          >
            <span>Manage / Prepare</span>
            <ArrowRight size={14} />
          </Button> */}

          {row.pdfUrl && (
            <button
              onClick={() => handleViewPdf(row)}
              className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-slate-100 rounded-md transition-colors"
              title="View PDF"
            >
              <Eye size={18} />
            </button>
          )}

          {row.status === 'Approved' && row.pdfUrl && (
            <button
              onClick={() => downloadPdfFromUrl(row.pdfUrl, `Quotation_${row.quotationNo || 'VK'}.pdf`)}
              className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-slate-100 rounded-md transition-colors"
              title="Download PDF"
            >
              <Download size={18} />
            </button>
          )}
        </div>
      ),
      cellClassName: '!whitespace-nowrap !py-1'
    }
  ];

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Top Navigation Bar & Customer Information Header Card commented out as per instructions */}
      {/* <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate('/ordering')}
          className="inline-flex items-center gap-2 px-3.5 py-2 bg-white hover:bg-slate-100 text-slate-700 rounded-xl border border-slate-200/80 shadow-sm font-medium text-sm transition-all"
        >
          <ArrowLeft size={16} />
          <span>Back to Master Orders</span>
        </button>

        <Button
          variant="primary"
          onClick={handleNewQuotation}
          rightIcon={<ArrowRight size={16} />}
          className="!bg-brand-blue hover:!bg-brand-blue/90 !px-5 !py-2.5 shadow-md hover:shadow-lg transition-all"
        >
          Create Quote
        </Button>
      </div> */}

      {/* <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-sm mb-4">
        <div className="flex items-center gap-3 flex-wrap pb-4 border-b border-slate-100">
          <h1 className="text-xl font-bold text-slate-800 tracking-tight">{customer.name}</h1>
          <span className="text-slate-300 font-bold">|</span>
          <span className="px-2.5 py-0.5 bg-brand-blue/10 text-brand-blue font-semibold text-xs rounded-md border border-brand-blue/20">
            ID: {customer.verveId}
          </span>
          <span className="text-slate-300 font-bold">|</span>
          <span className="text-sm font-medium text-slate-500">
            Lead Ordering & Lifecycle Workspace
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <div className="flex items-center gap-3 p-3 bg-slate-50/70 rounded-xl border border-slate-100">
            <Phone size={18} className="text-slate-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-slate-400 uppercase">Phone Number</p>
              <p className="text-sm font-semibold text-slate-700 truncate">{customer.phone || 'Not provided'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-slate-50/70 rounded-xl border border-slate-100">
            <Mail size={18} className="text-slate-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-slate-400 uppercase">Email Address</p>
              <p className="text-sm font-semibold text-slate-700 truncate">{customer.email || 'Not provided'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-slate-50/70 rounded-xl border border-slate-100">
            <MapPin size={18} className="text-slate-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-slate-400 uppercase">Location / Site</p>
              <p className="text-sm font-semibold text-slate-700 truncate">{customer.locationStr || 'Not provided'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-slate-50/70 rounded-xl border border-slate-100">
            <Package size={18} className="text-slate-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-slate-400 uppercase">Total Quotations</p>
              <p className="text-sm font-semibold text-slate-700 truncate">{quotations.length}</p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Developer Nithin 14-07-2026: Commented out upper part (Order Management header banner) not required */}
      <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm mb-4">
        <div className="pl-8 z-20">
          <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
            Master Orders
          </h1>
          <p className="text-sm font-body text-slate-400 mt-2 font-medium">Streamline your order management seamlessly.</p>
        </div>
        <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
          <img
            src="/kitchens/1.jpg"
            alt="Master Orders"
            className="absolute top-0 right-0 w-full h-full object-cover object-center opacity-90"
          />
        </div>
      </div>

      {/* Control panel outside table card for clean standalone spacing */}
      <div className="mb-4">
        <OrderingControlPanel
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          sortBy={sortBy}
          setSortBy={setSortBy}
          showAdvancedFilters={showAdvancedFilters}
          setShowAdvancedFilters={setShowAdvancedFilters}
          onAddClick={handleNewQuotation}
          addLabel="Create Order"
          onExport={handleExport}
          isExporting={isExporting}
          isWorkspace={true}
        />
      </div>

      {/* Quotations Table Section */}
      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200/80 flex flex-col overflow-hidden">
        {/* Commented out Quotations & Fulfillment Tracking header */}
        {/* <div className="py-3 px-4 border-b border-slate-200/80 bg-slate-50/50 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-800">Quotations & Fulfillment Tracking</h2>
          </div>
        </div> */}

        <div className="flex-1 overflow-auto">
          <DataTable
            columns={columns}
            data={paginatedData}
            isLoading={loading}
            loadingText="Loading Quotations..."
            emptyMessage="No quotations prepared for this lead yet. Click 'New Quotation' above to create one."
            rowClassName="hover:bg-slate-50/80 transition-colors !h-8"
          />
        </div>
      </div>
      {/* Pagination outside table container with 2 lines of gap (mt-6), visible unconditionally now */}
      {!loading && (
        <div className="mt-6 bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Timestamped Status History Modal */}
      <Modal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={`Status History: ${selectedHistoryRow?.quotationNo || 'Quotation'}`}
        className="max-w-xl w-full"
      >
        <div className="p-4">
          <div className="mb-4 pb-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">Current Lifecycle State</span>
            <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-brand-blue/10 text-brand-blue border border-brand-blue/20">
              {selectedHistoryRow?.orderStatus || 'Order Placed'}
            </span>
          </div>

          <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Clock size={16} className="text-brand-blue" />
            <span>Timestamped Lifecycle Log</span>
          </h4>

          <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
            {(!selectedHistoryRow?.statusHistory || selectedHistoryRow.statusHistory.length === 0) ? (
              <div className="p-4 bg-slate-50 rounded-xl text-center text-sm text-slate-500">
                No status changes logged yet.
              </div>
            ) : (
              [...selectedHistoryRow.statusHistory].reverse().map((item, idx) => {
                const dateObj = new Date(item.updatedAt);
                const dateFormatted = isNaN(dateObj.getTime()) ? item.updatedAt : dateObj.toLocaleDateString('en-IN', {
                  day: '2-digit', month: 'short', year: 'numeric'
                });
                const timeFormatted = isNaN(dateObj.getTime()) ? '' : dateObj.toLocaleTimeString('en-IN', {
                  hour: '2-digit', minute: '2-digit', hour12: true
                });

                return (
                  <div key={idx} className="flex items-start gap-3 p-3.5 bg-slate-50/80 hover:bg-slate-50 rounded-xl border border-slate-200/60 transition-colors">
                    <div className="w-2 h-2 rounded-full bg-brand-blue mt-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm text-slate-800">{item.status}</span>
                        <span className="text-xs font-medium text-slate-500 whitespace-nowrap bg-white px-2 py-0.5 rounded border border-slate-200">
                          {dateFormatted} {timeFormatted && `, ${timeFormatted}`}
                        </span>
                      </div>
                      {item.note && (
                        <p className="text-xs text-slate-600 mt-1">{item.note}</p>
                      )}
                      <p className="text-[11px] text-slate-400 mt-1">Updated By: {item.updatedBy || 'Admin'}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end">
            <Button variant="outline" onClick={() => setHistoryModalOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* PDF View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={selectedPdfDoc?.title || "Quotation Document"}
        className="max-w-6xl w-full"
      >
        <div className="-m-5 sm:-m-6 h-[85vh] flex flex-col rounded-b-lg sm:rounded-b-xl overflow-hidden">
          <PdfViewer
            selectedDocument={selectedPdfDoc}
            onClose={() => setIsViewModalOpen(false)}
          />
        </div>
      </Modal>
    </div>
  );
};
